import React, { useState, useEffect } from 'react';
import { Drawer, Button, Row, Col, Image, message, Progress, Space } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GET_DRIVER_VERIFICATION_DETAIL } from '../../../../graphql/queries/driverVerification/getDriverVerificationQueries';
import { REVIEW_DRIVER_VERIFICATION } from '../../../../graphql/mutations/driverVerification/driverVerificationMutations';
import ReviewModal from './ReviewModal';
import { useVehicleTypeOptions } from '../../../../hooks/useVehicleTypeOptions';

interface BatchReviewDrawerProps {
    visible: boolean;
    driverAccountIds: number[]; // 待审核的司机ID列表
    onClose: () => void;
    onComplete?: () => void; // 所有审核完成后的回调
}

const BatchReviewDrawer: React.FC<BatchReviewDrawerProps> = ({
    visible,
    driverAccountIds,
    onClose,
    onComplete,
}) => {
    const { t } = useTranslation();
    const vehicleTypeOptions = useVehicleTypeOptions();
    
    // 待审核队列（从传入的ID列表创建）
    const [pendingQueue, setPendingQueue] = useState<number[]>([]);
    // 当前正在审核的司机ID
    const [currentDriverId, setCurrentDriverId] = useState<number | null>(null);
    // 已完成的审核数量
    const [completedCount, setCompletedCount] = useState(0);
    
    // 查询当前司机详情（使用 lazy query，只在需要时查询）
    const [getDriverDetail, { data, loading, refetch }] = useLazyQuery(GET_DRIVER_VERIFICATION_DETAIL, {
        fetchPolicy: 'network-only',
    });
    
    // 审核 mutation
    const [reviewDriver, { loading: reviewLoading }] = useMutation(REVIEW_DRIVER_VERIFICATION);
    
    // 审核弹窗状态
    const [reviewModalVisible, setReviewModalVisible] = useState(false);
    const [reviewResult, setReviewResult] = useState<'APPROVED' | 'REJECTED' | null>(null);
    
    const detail = data?.getDriverVerificationDetail;
    
    // 加载下一个司机详情
    const loadNextDriver = (queue?: number[]) => {
        const targetQueue = queue || pendingQueue;
        if (targetQueue.length > 0) {
            const nextId = targetQueue[0];
            setCurrentDriverId(nextId);
            getDriverDetail({
                variables: {
                    driverAccountId: nextId,
                },
            });
        } else {
            // 队列为空，所有审核完成
            // 立即清空当前司机ID，避免显示错误进度
            setCurrentDriverId(null);
            message.success(t('riskControl.driverVerification.messages.batchReviewComplete'));
            // 先调用完成回调刷新列表，然后关闭抽屉
            if (onComplete) {
                onComplete();
            }
            // 延迟关闭抽屉，确保刷新完成
            setTimeout(() => {
                onClose();
            }, 100);
        }
    };
    
    // 初始化队列 - 当 visible 或 driverAccountIds 变化时重置
    useEffect(() => {
        if (visible && driverAccountIds.length > 0) {
            // 每次打开时都重新初始化，确保状态正确
            const initialQueue = [...driverAccountIds];
            setPendingQueue(initialQueue);
            setCurrentDriverId(null);
            setCompletedCount(0);
            // 自动加载第一个
            if (initialQueue.length > 0) {
                const firstId = initialQueue[0];
                setCurrentDriverId(firstId);
                getDriverDetail({
                    variables: {
                        driverAccountId: firstId,
                    },
                });
            }
        }
    }, [visible, driverAccountIds]);
    
    // 关闭时重置状态
    useEffect(() => {
        if (!visible) {
            setPendingQueue([]);
            setCurrentDriverId(null);
            setCompletedCount(0);
        }
    }, [visible]);
    
    // 处理审核按钮点击
    const handleApproveClick = () => {
        setReviewResult('APPROVED');
        setReviewModalVisible(true);
    };
    
    const handleRejectClick = () => {
        setReviewResult('REJECTED');
        setReviewModalVisible(true);
    };
    
    // 处理审核提交
    const handleReviewSubmit = async (reviewResult: 'APPROVED' | 'REJECTED', reviewComment?: string) => {
        if (!currentDriverId) {
            message.error(t('riskControl.driverVerification.messages.selectReviewResult'));
            return;
        }
        
        try {
            await reviewDriver({
                variables: {
                    driverAccountId: currentDriverId,
                    reviewResult: reviewResult,
                    reviewComment: reviewComment,
                },
            });
            message.success(t('riskControl.driverVerification.messages.reviewSuccess'));
            setReviewModalVisible(false);
            setReviewResult(null);
            
            // 从队列中移除当前司机
            setPendingQueue(prevQueue => {
                const newQueue = prevQueue.filter(id => id !== currentDriverId);
                setCompletedCount(prev => prev + 1);
                
                // 如果队列为空，立即清空当前司机ID，避免显示错误进度
                if (newQueue.length === 0) {
                    setCurrentDriverId(null);
                }
                
                // 自动加载下一个
                setTimeout(() => {
                    loadNextDriver(newQueue);
                }, 500); // 短暂延迟，让用户看到成功提示
                
                return newQueue;
            });
            
        } catch (error: any) {
            message.error(error.message || t('riskControl.driverVerification.messages.reviewFailed'));
        }
    };
    
    // 跳过当前司机
    const handleSkip = () => {
        if (!currentDriverId) return;
        
        setPendingQueue(prevQueue => {
            const newQueue = prevQueue.filter(id => id !== currentDriverId);
            loadNextDriver(newQueue);
            return newQueue;
        });
    };
    
    // 计算进度：基于队列状态，而不是 completedCount
    const totalCount = driverAccountIds.length;
    const remainingCount = pendingQueue.length;
    // 如果队列为空且没有当前司机，说明已完成，显示完成状态
    // 如果队列为空但有当前司机，说明正在处理最后一个，currentIndex = totalCount
    // 如果队列不为空，currentIndex = totalCount - remainingCount + 1
    const currentIndex = remainingCount === 0 
        ? (currentDriverId ? totalCount : totalCount) 
        : totalCount - remainingCount + 1;
    const progress = totalCount > 0 ? Math.round(((totalCount - remainingCount) / totalCount) * 100) : 0;
    
    if (!visible) {
        return null;
    }
    
    // 格式化手机号（带区号）
    const formatPhone = (phone: string, countryCode?: number) => {
        if (countryCode && countryCode !== 0) {
            return `+${countryCode} ${phone}`;
        }
        return phone;
    };
    
    // 标题：司机ID———电话号(姓名)
    const pageTitle = detail 
        ? `${detail.driverId}———${formatPhone(detail.phone, detail.countryCode)}(${detail.name})`
        : t('riskControl.driverVerification.batchReview.loading');
    
    return (
        <Drawer
            title={
                <div className="flex items-center justify-between w-full">
                    <span>{t('riskControl.driverVerification.batchReview.title')}</span>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-500">
                            {t('riskControl.driverVerification.batchReview.progress', {
                                current: currentIndex,
                                total: totalCount,
                            })}
                        </span>
                        <Progress 
                            percent={progress} 
                            size="small" 
                            style={{ width: 150 }}
                        />
                    </div>
                </div>
            }
            open={visible}
            onClose={onClose}
            width={1200}
            extra={
                <Space>
                    <Button onClick={handleSkip}>
                        {t('riskControl.driverVerification.batchReview.skip')}
                    </Button>
                    <Button onClick={onClose}>
                        {t('riskControl.driverVerification.batchReview.cancel')}
                    </Button>
                </Space>
            }
        >
            {loading ? (
                <div className="flex items-center justify-center h-96">
                    <div>{t('riskControl.driverVerification.batchReview.loading')}</div>
                </div>
            ) : !detail ? (
                <div className="flex items-center justify-center h-96">
                    <div>{t('riskControl.driverVerification.batchReview.noData')}</div>
                </div>
            ) : (
                <div className="p-6">
                    {/* 顶部：标题和操作按钮 */}
                    <div className="flex items-center justify-between mb-8">
                        <h1 className="text-lg font-semibold m-0">{pageTitle}</h1>
                        
                        {/* 右上角操作按钮（垂直排列） */}
                        <div className="flex flex-col gap-2">
                            <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                onClick={handleApproveClick}
                                className="bg-green-500 hover:bg-green-600 border-green-500 text-white"
                                style={{ minWidth: '100px' }}
                                loading={reviewLoading}
                            >
                                {t('riskControl.driverVerification.reviewModal.approve')}
                            </Button>
                            <Button
                                danger
                                icon={<CloseOutlined />}
                                onClick={handleRejectClick}
                                style={{ minWidth: '100px' }}
                                loading={reviewLoading}
                            >
                                {t('riskControl.driverVerification.reviewModal.reject')}
                            </Button>
                        </div>
                    </div>
                    
                    {/* 上半部分：司机信息（左右两列布局） */}
                    <Row gutter={32} className="mb-8">
                        {/* 左列：司机基本信息 */}
                        <Col span={12}>
                            <div className="space-y-5">
                                <div className="flex items-center">
                                    <span className="w-28 text-gray-600">{t('riskControl.driverVerification.detail.driverId')}:</span>
                                    <span className="text-gray-900 flex-1">{detail.driverId}</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="w-28 text-gray-600">{t('riskControl.driverVerification.detail.driverName')}:</span>
                                    <span className="text-gray-900 flex-1">{detail.name}</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="w-28 text-gray-600">{t('riskControl.driverVerification.detail.driverPhone')}:</span>
                                    <span className="text-gray-900 flex-1">{formatPhone(detail.phone, detail.countryCode)}</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="w-28 text-gray-600">{t('riskControl.driverVerification.detail.affiliatedFleet')}:</span>
                                    <span className="text-gray-900 flex-1">{detail.dspName || '-'}</span>
                                </div>
                            </div>
                        </Col>
                        
                        {/* 右列：证件和车辆信息 */}
                        <Col span={12}>
                            <div className="space-y-5">
                                <div className="flex items-center">
                                    <span className="w-36 text-gray-600">{t('riskControl.driverVerification.detail.idDocumentNumber')}:</span>
                                    <span className="text-gray-900 flex-1">{detail.idDocumentNumber || '-'}</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="w-36 text-gray-600">{t('riskControl.driverVerification.detail.licensePlateNumber')}:</span>
                                    <span className="text-gray-900 flex-1">{detail.vehiclePlate || '-'}</span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                    
                    {/* 下半部分：照片区域（左右两列布局） */}
                    <Row gutter={32}>
                        {/* 左列：驾照照片（证件照片） */}
                        <Col span={12}>
                            <div>
                                <h3 className="text-base font-semibold mb-4">
                                    {t('riskControl.driverVerification.detail.idDocumentPhotos')}
                                </h3>
                                {detail.idDocumentImageUrls && detail.idDocumentImageUrls.length > 0 ? (
                                    <div className="grid grid-cols-2 gap-3">
                                        {detail.idDocumentImageUrls.map((url: string, index: number) => (
                                            <div key={index} className="aspect-square border border-gray-300 rounded overflow-hidden bg-gray-50">
                                                <Image
                                                    src={url}
                                                    alt={`ID Document ${index + 1}`}
                                                    className="w-full h-full object-cover"
                                                    preview={{
                                                        mask: <div className="flex items-center justify-center text-white text-sm">点击预览</div>
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-gray-400 py-8 text-center">{t('common.noPhotosAvailable')}</div>
                                )}
                            </div>
                        </Col>
                        
                        {/* 右列：车辆照片 */}
                        <Col span={12}>
                            <div>
                                <h3 className="text-base font-semibold mb-4">
                                    {t('riskControl.driverVerification.detail.vehiclePhotos')}
                                </h3>
                                {detail.vehicleImageUrls && detail.vehicleImageUrls.length > 0 ? (
                                    <div className="grid grid-cols-2 gap-3">
                                        {detail.vehicleImageUrls.map((url: string, index: number) => (
                                            <div key={index} className="aspect-square border border-gray-300 rounded overflow-hidden bg-gray-50">
                                                <Image
                                                    src={url}
                                                    alt={`Vehicle ${index + 1}`}
                                                    className="w-full h-full object-cover"
                                                    preview={{
                                                        mask: <div className="flex items-center justify-center text-white text-sm">点击预览</div>
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-gray-400 py-8 text-center">{t('common.noPhotosAvailable')}</div>
                                )}
                            </div>
                        </Col>
                    </Row>
                    
                    {/* 审核弹窗 */}
                    {reviewModalVisible && (
                        <ReviewModal
                            visible={reviewModalVisible}
                            driverAccountId={currentDriverId}
                            reviewResult={reviewResult || undefined}
                            onCancel={() => {
                                setReviewModalVisible(false);
                                setReviewResult(null);
                            }}
                            onSubmit={handleReviewSubmit}
                            loading={reviewLoading}
                        />
                    )}
                </div>
            )}
        </Drawer>
    );
};

export default BatchReviewDrawer;

